import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconClients, IconUserPlus, IconError } from '../icons';
import { useAuth } from '../context/AuthContext';
import { canUseKanzleiCockpit, isKanzleiAccount } from '../config/kanzleiAccess';
import { ADVISOR_LANDING_ENABLED } from '../config/advisorLandingAccess';
import KanzleiOnboardingStepper from '../components/advisor/KanzleiOnboardingStepper';
import {
  getAdvisorCockpitMandanten,
  type CockpitMandantSummary,
} from '../api/advisorCockpitApi';
import { DataTable, type Column } from '../components/common/DataTable';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import {
  MandantRow,
  AmpelCountCell,
  CheckedCell,
} from '../components/advisor/MandantRow';

/**
 * M-UX-7.2 — Mandanten-Übersicht `/dashboard/mandanten`
 * (`plan-ux-7.2-mandanten.md` §2, `architecture-ux-restructure.md` §13.3).
 *
 * „Alle Mandate auf einen Blick" für die Kanzlei. Die Seite ist READ-ONLY und
 * löst KEINEN neuen Backend-Call aus außer dem bestehenden
 * `getAdvisorCockpitMandanten` (nur für Beta-Advisor).
 *
 * **Vollständigkeit (E3, kritisch):** Die Basis-Liste sind ALLE Advisor-
 * Mitgliedschaften aus `availableTenants` (`role === 'advisor'`). Die
 * Ampel-Summary wird per LEFT JOIN (`tenant.scopeUid === summary.mandantUid`,
 * E4) dazugerechnet. Der Server liefert die Summary NUR für Beta-Mandanten —
 * Nicht-Beta-Mandate dürfen daher NICHT aus der Übersicht verschwinden; sie
 * erscheinen als Zeile ohne Ampel („—"). Ein Summary-Ausfall (`error`) leert
 * die Mandatsliste ebenfalls NICHT (fail-open fürs Read-only-Anzeigen,
 * fail-closed für die Ampel-Werte).
 *
 * **Zwei Datenlage-Stufen (E5):**
 *  - Beta-Advisor (`isLoopBBeta`) ⇒ Call + Ampel-Spalten (Left-Join).
 *  - Nicht-Beta-Advisor ⇒ KEIN Call, degradierte Spalten (Mandant · Aktionen).
 *
 * @module pages/MandantenPage
 */

/** Eine gerenderte Mandanten-Zeile (Basis = Advisor-Membership, Summary via Left-Join). */
interface MandantRowData {
  /** Stabiler React-Key (Tenant-Id, oder `orphan:<uid>` bei der Defensive-Zeile). */
  key: string;
  /** Aufgelöste Anzeigename-Fallback-Kette (§1). */
  displayName: string;
  /** Ampel-Summary oder `null` (kein Join-Treffer / Nicht-Beta / Ausfall). */
  summary: CockpitMandantSummary | null;
  /** `null` NUR bei der verwaisten Defensive-Zeile (E4) ⇒ Aktionen disabled. */
  tenantId: string | null;
}

/** Lade-/Fehlerzustand des (nur für Beta ausgelösten) Summary-Calls. */
type SummaryState =
  | { status: 'idle' } // Nicht-Beta ⇒ kein Call
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; summaries: CockpitMandantSummary[] };

// M-ACF-5 (`architecture-advisor-cockpit-first.md` §7.8): Shell-Flag-Read zur
// Modul-Ladezeit (Muster `TenantSwitcher.tsx:12`). Der Header-CTA „Zu meiner
// Kanzlei" ist zusätzlich an das Landing-Flag geknüpft (`featureOn` unten).
const UX_SHELL_ENABLED = process.env.REACT_APP_UX_SHELL_ENABLED === 'true';

/**
 * Die Mandanten-Übersichtsseite.
 *
 * @returns die gerenderte Seite (Empty-State, Ledger-Tabelle oder degradierte
 *   Liste, je nach Datenlage).
 */
const MandantenPage: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { currentUser, sellerData, availableTenants, switchTenant, activeRole } = useAuth();

  // M-ACF-5 (§3.4 / §7.8): der Header-CTA in den Owner-Modus ist nur relevant,
  // wenn das Landing-Feature scharf ist UND der Advisor NICHT im Mandanten-Modus
  // ist — dort übernimmt die `AdvisorContextBar` diese Funktion bereits (I9),
  // ein zweiter Button wäre redundant.
  const showBackToOwnCta =
    ADVISOR_LANDING_ENABLED && UX_SHELL_ENABLED && activeRole !== 'advisor';

  const isBeta = canUseKanzleiCockpit(currentUser?.uid, sellerData);

  // M-OB-13 (§8.2): frische Kanzlei ohne Mandat ⇒ Onboarding-Stepper statt des
  // generischen Empty-States. `isKanzleiAccount` ist rein kosmetisch (UX-Signal).
  const isKanzlei = isKanzleiAccount(sellerData);

  const advisorTenants = useMemo(
    () => availableTenants.filter((tenant) => tenant.role === 'advisor'),
    [availableTenants],
  );

  const [summaryState, setSummaryState] = useState<SummaryState>(
    isBeta ? { status: 'loading' } : { status: 'idle' },
  );

  const loadSummaries = useCallback(() => {
    // Fail-closed: Nicht-Beta löst NIE einen Server-Call aus (E5b, §13.4
    // Performance-Gate). Der Server erzwingt den Scope ohnehin ein zweites Mal.
    if (!isBeta) {
      setSummaryState({ status: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setSummaryState({ status: 'loading' });
    getAdvisorCockpitMandanten()
      .then((res) => {
        if (!cancelled) setSummaryState({ status: 'ready', summaries: res.mandanten });
      })
      .catch(() => {
        if (!cancelled) setSummaryState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [isBeta]);

  useEffect(() => loadSummaries(), [loadSummaries]);

  const summaries = summaryState.status === 'ready' ? summaryState.summaries : [];

  const summaryByUid = useMemo(() => {
    const map = new Map<string, CockpitMandantSummary>();
    for (const summary of summaries) {
      map.set(summary.mandantUid, summary);
    }
    return map;
  }, [summaries]);

  const rows = useMemo<MandantRowData[]>(() => {
    // Basis: jede Advisor-Membership wird zur Zeile — Left-Join der Summary.
    const base: MandantRowData[] = advisorTenants.map((tenant) => {
      const summary = summaryByUid.get(tenant.scopeUid) ?? null;
      return {
        key: tenant.tenantId,
        displayName: summary?.displayName ?? tenant.displayName ?? t('mandanten.unnamedClient'),
        summary,
        tenantId: tenant.tenantId,
      };
    });

    // Defensive (E4): Summary OHNE passende Advisor-Membership (Server löst
    // Memberships selbst auf ⇒ praktisch unmöglich). Wird angehängt statt
    // verworfen — nichts verschwindet; Aktionen deaktiviert (tenantId null).
    const knownScopeUids = new Set(advisorTenants.map((tenant) => tenant.scopeUid));
    const orphans: MandantRowData[] = summaries
      .filter((summary) => !knownScopeUids.has(summary.mandantUid))
      .map((summary) => ({
        key: `orphan:${summary.mandantUid}`,
        displayName: summary.displayName ?? t('mandanten.unnamedClient'),
        summary,
        tenantId: null,
      }));

    return [...base, ...orphans];
  }, [advisorTenants, summaryByUid, summaries, t]);

  /**
   * Row-Click / Enter = „Cockpit öffnen" (primäre Aktion, §2.2): erst den
   * Tenant wechseln (`switchTenant`, synchron), dann zum Cockpit navigieren —
   * in dieser Reihenfolge (E4). No-op bei der verwaisten Defensive-Zeile.
   */
  const openCockpit = useCallback(
    (row: MandantRowData) => {
      if (row.tenantId === null) return;
      switchTenant(row.tenantId);
      navigate('/dashboard/cockpit');
    },
    [switchTenant, navigate],
  );

  const isLoading = isBeta && summaryState.status === 'loading';
  const isError = isBeta && summaryState.status === 'error';

  const columns = useMemo<Column<MandantRowData>[]>(() => {
    // Spalten-Köpfe brechen in KEINER der 6 Sprachen mitten im Wort um (Ziel 5):
    // die Label werden in `whitespace-nowrap` gehüllt (auch innerhalb des
    // Sortier-Buttons, den DataTable um sortierbare Header legt).
    const label = (key: string) => <span className="whitespace-nowrap">{t(key)}</span>;

    const nameColumn: Column<MandantRowData> = {
      id: 'name',
      header: label('mandanten.col.name'),
      accessor: (row) => (
        <span className="whitespace-nowrap font-medium text-primary">{row.displayName}</span>
      ),
      sortValue: (row) => row.displayName,
    };
    const actionsColumn: Column<MandantRowData> = {
      id: 'actions',
      header: <span className="sr-only">{t('mandanten.col.actions')}</span>,
      align: 'right',
      accessor: (row) => (
        <MandantRow displayName={row.displayName} summary={row.summary} tenantId={row.tenantId} />
      ),
    };

    // Nicht-Beta (degraded, E5b): keine Ampel-Spalten, kein Call.
    if (!isBeta) {
      return [nameColumn, actionsColumn];
    }

    // Beta: sortierbare Ledger-Ampel-Spalten (Exceptions-first, §2.3/§7a).
    // Zeilen ohne Summary sortieren via `null`-sortValue ans Ende (DataTable
    // schiebt `null` nach unten). Während `isLoading` zeigen die Zellen einen
    // Skeleton — die Mandats-Zeilen selbst (Name/Aktionen) stehen sofort.
    return [
      nameColumn,
      {
        id: 'red',
        header: label('mandanten.col.red'),
        numeric: true,
        align: 'right',
        accessor: (row) => <AmpelCountCell kind="red" summary={row.summary} loading={isLoading} />,
        sortValue: (row) => (row.summary ? row.summary.redCount : null),
      },
      {
        id: 'yellow',
        header: label('mandanten.col.yellow'),
        numeric: true,
        align: 'right',
        accessor: (row) => <AmpelCountCell kind="yellow" summary={row.summary} loading={isLoading} />,
        sortValue: (row) => (row.summary ? row.summary.yellowCount : null),
      },
      {
        id: 'checked',
        header: label('mandanten.col.checked'),
        numeric: true,
        align: 'right',
        accessor: (row) => <CheckedCell summary={row.summary} loading={isLoading} />,
        sortValue: (row) => (row.summary ? row.summary.totalChecked : null),
      },
      actionsColumn,
    ];
  }, [isBeta, isLoading, t]);

  return (
    <div className="min-h-screen bg-surface-base p-4 md:p-6">
      <div className="mx-auto max-w-5xl">
        {/* F2-Fix (Review-Re-Loop): wörtliche Inert-Garantie — bei Flag off
            (kein CTA) bleibt das Header-Markup byte-identisch zum Status quo
            (kein zusätzlicher Wrapper-`div`, kein `justify-between`). Der
            Wrapper/`justify-between` wird NUR eingeführt, wenn der CTA
            tatsächlich gerendert wird. */}
        {showBackToOwnCta ? (
          <header className="mb-6 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-surface-inset text-accent">
                <IconClients size={20} />
              </span>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold leading-tight text-primary">{t('mandanten.title')}</h1>
                <p className="mt-0.5 text-sm text-secondary">{t('mandanten.subtitle')}</p>
              </div>
            </div>
            {/* M-ACF-5 (§3.4 / §7.8): Rückweg in den eigenen Owner-Modus. Reines
                `navigate('/dashboard')` — im Kanzlei-Kontext ist der eigene Tenant
                bereits aktiv, KEIN `switchTenant` nötig. Kein Bounce: der
                Session-Marker ist zu diesem Zeitpunkt immer gestempelt (Gate- oder
                MainLayout-Stempel). Label = geteilter `advisorBar.backToOwn`-Key
                (6 Sprachen, kein neuer Key). */}
            <Button
              variant="secondary"
              className="shrink-0"
              onClick={() => navigate('/dashboard')}
            >
              {t('advisorBar.backToOwn')}
            </Button>
          </header>
        ) : (
          <header className="mb-6 flex items-center gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-surface-inset text-accent">
              <IconClients size={20} />
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold leading-tight text-primary">{t('mandanten.title')}</h1>
              <p className="mt-0.5 text-sm text-secondary">{t('mandanten.subtitle')}</p>
            </div>
          </header>
        )}

        {isKanzlei && advisorTenants.length === 0 ? (
          // M-OB-13 (§8.2): Kanzlei-Onboarding-Stepper als geführter Empty-State
          // für eine frisch registrierte Kanzlei ohne Mandat. Ersetzt den
          // generischen Empty-State (unten) nur für accountType==='kanzlei'.
          <KanzleiOnboardingStepper />
        ) : rows.length === 0 ? (
          // Leerzustand als Wachstums-Einladung (D4), nicht als Fehlerton:
          // großzügiger Icon-Chip, Nutzenversprechen, EIN primärer Mint-CTA.
          <Card as="section" className="text-center">
            <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-10">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-surface-inset text-accent">
                <IconClients size={28} />
              </span>
              <div className="flex flex-col gap-1.5">
                <h2 className="text-lg font-semibold text-primary">{t('mandanten.empty.title')}</h2>
                <p className="text-sm leading-relaxed text-secondary">{t('mandanten.empty.body')}</p>
              </div>
              <Button
                variant="primary"
                className="mt-1"
                icon={<IconUserPlus aria-hidden />}
                onClick={() => navigate('/dashboard/konto/mandanten-einladen')}
              >
                {t('mandanten.empty.cta')}
              </Button>
            </div>
          </Card>
        ) : (
          <>
            {isError && (
              // Ruhig-informativ (D4), kein Alarm-Rot: die Mandatsliste bleibt
              // sichtbar, nur die Ampel-Werte fehlen. Amber-Glyph signalisiert
              // „Hinweis", nicht „kaputt".
              <div
                role="alert"
                className="mb-4 flex flex-col gap-3 rounded-card border border-default bg-surface-raised p-4 text-sm text-secondary sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="flex items-start gap-2.5 sm:items-center">
                  <IconError
                    size={16}
                    className="mt-0.5 shrink-0 text-[var(--tone-warning-fg)] sm:mt-0"
                  />
                  <span>{t('mandanten.error')}</span>
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  className="shrink-0 self-start sm:self-auto"
                  onClick={loadSummaries}
                >
                  {t('mandanten.retry')}
                </Button>
              </div>
            )}

            {/* Prüf-Summary lädt asynchron (per-Beta Left-Join): der Roster steht
                sofort, nur die Ampel-Zellen zeigen einen Skeleton (kein voller
                Seiten-Spinner, kein Layout-Shift, Ziel 6). Die Lade-Ansage für
                Assistive-Tech sitzt zentral hier, die Zell-Skeletons sind
                `aria-hidden`. */}
            {isLoading && (
              <span role="status" className="sr-only">
                {t('mandanten.loading')}
              </span>
            )}
            <DataTable<MandantRowData>
              columns={columns}
              rows={rows}
              rowKey={(row) => row.key}
              onRowClick={openCockpit}
              initialSort={isBeta ? { id: 'red', dir: 'desc' } : undefined}
              empty={t('mandanten.empty.title')}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MandantenPage;
